'use client';

import { useController } from 'react-hook-form';
import type { DeepMap, FieldError, FieldValues, UseControllerProps } from 'react-hook-form';
import formStyle from '../../../styles/modules/form.module.scss';
import type { VariantType } from '../../../types/variant';
import FormLabel from '../FormLabel/FormLabel';
import fanychanceStyles from './FormSelect.fanychance.module.scss';
import styles from './FormSelect.module.scss';
import raffleStyles from './FormSelect.raffle.module.scss';

export type FormSelectProps<T extends FieldValues> = {
  label?: string;
  placeholder?: string;
  items: Array<{ value: string | number; text: string }>;
  required?: boolean;
  outlined?: boolean;
  onChange?: () => void;
  hint?: string;
  variant?: VariantType;
  disabled?: boolean;
} & UseControllerProps<T>;

function FormSelect<T extends FieldValues>({
  name,
  items,
  label,
  placeholder,
  required = false,
  control,
  rules,
  outlined = false,
  onChange,
  hint,
  variant = 'default',
  disabled = false,
}: FormSelectProps<T>) {
  const options = items.map((v) => (
    <option key={v.value} value={v.value}>
      {v.text}
    </option>
  ));

  const {
    field,
    fieldState: { error },
  } = useController<T>({ name, control, rules });

  // variantに応じてスタイルを決定
  // var(--color-primary)を使っているので分ける
  const componentStyles = variant === 'raffle' ? raffleStyles : variant === 'fanychance' ? fanychanceStyles : styles;

  return (
    <div className={formStyle.formComponentStyle}>
      {/* ラベル */}
      {label && <FormLabel>{label}</FormLabel>}
      {/* フィールドブロック */}
      <div className={error && formStyle.hasError}>
        <div
          className={`${formStyle.fieldWrapper} ${componentStyles.selectWrapper} ${outlined ? componentStyles.outlined : ''}`}
        >
          <select
            required={required}
            name={field.name}
            ref={field.ref}
            onBlur={field.onBlur}
            onChange={(e) => {
              field.onChange(e);
              onChange?.();
            }}
            value={field.value}
            className={`${componentStyles.select} ${!field.value && componentStyles.placeholder}`}
            disabled={disabled}
          >
            {/* placeholderは1つ目の選択肢扱いにして表示 */}
            {placeholder && (
              <option style={{ display: 'none' }} value={undefined}>
                {placeholder}
              </option>
            )}
            {options}
          </select>
          <div className={componentStyles.appearance} />
        </div>
        {hint && <p className={formStyle.hint}>{hint}</p>}
        {/* エラー */}
        {error && <p className={formStyle.validateErrors}>{(error as DeepMap<FieldValues, FieldError>).message}</p>}
      </div>
    </div>
  );
}

export default FormSelect;
